/**
 * Автоматический перенос незакрытых задач с прошедших дней на сегодня.
 *
 * Живёт в каркасе приложения, а не на экране. Пока перенос вызывался из
 * календаря, невыполненное вчера доезжало до сегодня только у того, кто в этот
 * заход открывал календарь. Вернувшийся на вкладке задач видел вчерашние дела в
 * группе «Просрочено» — то есть в сегодняшнем дне, — но ни в одно сегодняшнее
 * число они не входили: ни в бюджет времени, ни в счётчик «сделано», ни в
 * счётчик вкладки. Показывать задачу в дне и не считать её в этом дне нельзя:
 * человек сверяет одно с другим и находит расхождение.
 *
 * Ровно по той же причине здесь же, в каркасе, заводятся напоминания.
 *
 * Перенос повторяется с наступлением новых суток, а не только при запуске.
 * Телефон не перезапускает приложение каждое утро — оно просыпается из фона, —
 * и вчерашние задачи оставались вчерашними, пока приложение не закрывали
 * вручную.
 */

import { useEffect, useRef } from 'react';

import { useProfile } from '@/entities/profile/model/use-profile';
import { rolloverOverdueTasks } from '@/entities/task/model/task-service';
import { type IsoDate, plural } from '@/shared/lib/datetime';
import { useToday } from '@/shared/lib/use-today';
import { toast } from '@/shared/ui/toast';

export function useRollover(): void {
  const profile = useProfile();
  const autoRollover = profile?.autoRollover;
  const today = useToday();
  /** День, на который перенос уже сделан: один раз на сутки. */
  const doneFor = useRef<IsoDate | null>(null);

  useEffect(() => {
    // Профиль приходит асинхронно — до него не понятно, включён ли перенос.
    if (autoRollover === undefined || doneFor.current === today) return;
    doneFor.current = today;
    if (!autoRollover) return;

    void rolloverOverdueTasks(today).then(({ movedCount, claimedCount, stuckTasks }) => {
      if (movedCount === 0) return;

      /*
        Про задачи с прошедшим дедлайном сказать нужно отдельно: дня у них не
        было вовсе, и в сегодняшнем плане они появляются впервые. Молча менять
        состав дня нельзя — человек не поймёт, откуда взялось лишнее время.

        Когда переехали только они, отдельная приписка уже ничего не добавляет:
        «перенесена 1 задача. 1 — с прошедшим дедлайном» пересчитывает одно и
        то же дважды.
      */
      const moved = `${movedCount} ${plural(movedCount, 'задача', 'задачи', 'задач')}`;
      const verb = plural(movedCount, 'перенесена', 'перенесены', 'перенесено');

      const head =
        claimedCount === movedCount
          ? `На сегодня ${verb} ${moved} с прошедшим дедлайном.`
          : claimedCount > 0
            ? `На сегодня ${verb} ${moved}. ${claimedCount} — с прошедшим дедлайном.`
            : `На сегодня ${verb} ${moved}.`;

      const stuckHint =
        stuckTasks.length > 0
          ? ` ${stuckTasks.length} из них висит давно — стоит пересмотреть`
          : '';

      toast.info(head + stuckHint);
    });
  }, [autoRollover, today]);
}
